/**
 * ClauseGuard Analytics Client
 * Lightweight client-side event tracking
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Event types matching the server
export type AnalyticsEventType =
  | 'page_view'
  | 'analysis_started'
  | 'analysis_completed'
  | 'analysis_failed'
  | 'checkout_started'
  | 'checkout_completed'
  | 'signup'
  | 'login'
  | 'error';

// Session ID for grouping events
let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
  return sessionId;
}

/**
 * Track an analytics event
 * Fire-and-forget - doesn't block or throw errors
 */
export function trackEvent(
  eventType: AnalyticsEventType,
  properties?: Record<string, unknown>
): void {
  // Don't block the main thread
  setTimeout(() => {
    fetch(`${API_BASE}/api/analytics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: eventType,
        session_id: getSessionId(),
        properties,
        path: window.location.pathname,
        referrer: document.referrer || undefined,
      }),
    }).catch(() => {
      // Silently fail - analytics shouldn't affect user experience
    });
  }, 0);
}

/**
 * Track a page view
 */
export function trackPageView(pageName?: string): void {
  trackEvent('page_view', { page: pageName || window.location.pathname });
}

/**
 * Track analysis started
 */
export function trackAnalysisStarted(filename: string, fileSize: number): void {
  trackEvent('analysis_started', { filename, file_size: fileSize });
}

/**
 * Track analysis completed
 */
export function trackAnalysisCompleted(
  clausesCount: number,
  highRisk: number,
  mediumRisk: number,
  lowRisk: number
): void {
  trackEvent('analysis_completed', {
    clauses_count: clausesCount,
    high_risk: highRisk,
    medium_risk: mediumRisk,
    low_risk: lowRisk,
  });
}

/**
 * Track analysis failed
 */
export function trackAnalysisFailed(error: string): void {
  trackEvent('analysis_failed', { error });
}

/**
 * Track checkout started
 */
export function trackCheckoutStarted(plan: string): void {
  trackEvent('checkout_started', { plan });
}

/**
 * Track checkout completed
 */
export function trackCheckoutCompleted(plan: string): void {
  trackEvent('checkout_completed', { plan });
}

/**
 * Track error
 */
export function trackError(errorType: string, message: string): void {
  trackEvent('error', { error_type: errorType, message });
}
